<!-- Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved. -->
<div *ngIf="(isMSP$ | async); else notMSP"
     class="msps-have-no-subscriptions">{{ 'downloads.msps-have-no-downloads' | translate }}
</div>

<ng-template #notMSP>
    <div *ngIf="(loadingSubscriptions$ | async) && (subscriptions$ | async) as subscriptions; else skeletonLoaderForSubs;"
         id="downloads-page" class="downloads-page-wrapper"
         appLifecycleTracker trackerName="downloads-page">
        <div class="header">
            <mat-form-field class="subscription-select-form-field" appearance="outline">
                <mat-select #selectedSubCtrl
                            [value]="selectedSubscription$ | async"
                            placeholder="{{'L_SUBS_PHODLER' | translate}}"
                            (selectionChange)="onSubscriptionSelectionChange($event.value)">
                    <!-- First option is only to handle no subscriptions -->
                    <mat-option *ngIf="0 === subscriptions.length; else subscriptionsAvailable;"
                                [value]="null" id="no-subscription-found"
                                [disabled]="true">
                        <h4 mat-line>{{ 'L_NONE' | translate }}</h4>
                    </mat-option>
                    <ng-template #subscriptionsAvailable>
                        @for (subscription of subscriptions; track subscription.id) {
                            <mat-option id="multiple-option" class="multiple-option"
                                        [disabled]="disabledSubscription(subscription)"
                                        [value]="subscription">
                                <h4 matListItemTitle>{{ subscription.stripe_product.name }} </h4>
                                <h5 matListItemLine>{{ subscription.subscription_id }} </h5>
                                <h5 matListItemMeta>
                                    ({{ subscription.count - subscription.used }} {{ 'SD_LICENSES_AVAILABLE' | translate }})
                                    @if (isSubExpired(subscription)) {
                                        {{ 'EXPIRED' | translate }}
                                    }
                                    @if (subscription?.stripe_product?.tier === StripeProductTier.HARDWARE) {
                                        Hardware
                                    }
                                </h5>
                            </mat-option>
                        }
                    </ng-template>
                </mat-select>
            </mat-form-field>
            <a class="subscriptions-link manage-subscriptions-link" routerLink="/products"
               aria-label="Go to your products">
                <span>{{ 'downloads.your-products-link' | translate }}</span>
            </a>
        </div>

        <ng-template #windowsHeader>
            <div class="section-header">
                <span class="mdi mdi-microsoft-windows os-icon icon"></span>
                <span class="os-title">Windows</span>
            </div>
        </ng-template>

        <ng-template #linuxHeader>
            <div class="section-header">
                <span class="mdi mdi-linux os-icon icon"></span>
                <span class="os-title">Linux</span>
            </div>
        </ng-template>

        <ng-template #nonOsSpecificHeader>
            <div class="section-header">
                <span class="mdi mdi-desktop-tower os-icon icon"></span>
                <span class="os-title">{{ 'downloads.other-os.title' | translate }}</span>
            </div>
        </ng-template>

        <ng-template #fdeSubscription let-softwareVersion="softwareVersion">
            <mat-card appearance="raised">
                <mat-card-content>
                    <ng-container *ngTemplateOutlet="windowsHeader"></ng-container>
                    <!-- FDE Installers -->
                    @for (winFdeVersion of softwareVersion?.win_fde_versions; track $index) {
                        <div class="download-wrapper">
                            <span class="mdi mdi-download download-icon icon"></span>
                            <span (click)="onDownloadAsset(selectedSubCtrl.value, winFdeVersion.url, 'download_win_fde')"
                                  class="clickable pseudo-link">{{ winFdeVersion.name }} {{ winFdeVersion.version }}
                            </span>&nbsp;(EXE)
                        </div>
                        <div *ngIf="winFdeVersion.url !== null" class="text-content">
                            <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                            <a class="link" href="{{freshDeskFdeGuideUrl}}" target="_blank"
                               rel="noopener noreferrer">{{ freshDeskFdeGuideUrl }}</a>
                        </div>
                        <div class="download-wrapper">
                            <span class="mdi mdi-download download-icon icon"></span>
                            <span (click)="onDownloadAsset(selectedSubCtrl.value, winFdeVersion.msi_url, 'download_win_fde')"
                                  class="clickable pseudo-link">{{ winFdeVersion.name }} {{ winFdeVersion.version }}
                            </span>&nbsp;{{'downloads.msi.command-line-only' | translate}}
                        </div>
                        <div class="text-content">
                            <span>{{'downloads.windows.installation-command' | translate}} {{ getWinFdeInstallerCommand(winFdeVersion.msi_url) }}</span>
                            <span class="mdi mdi-content-copy clickable icon copy-icon"
                                  matTooltip="{{'downloads.copy-install-command-tooltip' | translate}}"
                                  matTooltipPosition="right"
                                  (click)="onCopyWinFdeInstallerCommand(selectedSubCtrl.value, winFdeVersion.msi_url)">
                            </span>
                            <p>{{'downloads.windows.parameter-details' | translate}}</p>
                        </div>
                    }
                </mat-card-content>
            </mat-card>
        </ng-template>

        <ng-template #activationCodes let-softwareVersion="softwareVersion">
            <div class="section-header-content">
                <div class="text-content">{{ 'downloads.windows.activation-code' | translate }} -
                    <span id="activationCodeValue">
                        {{ softwareVersion.feature_flags }}_{{ selectedSubCtrl.value.uuid }}_{{ softwareVersion.system }}
                    </span>
                    <span id="copyActivationCodeButton"
                          class="mdi mdi-content-copy clickable icon copy-icon"
                          matTooltip="{{'downloads.copy-activation-code-tooltip' | translate}}"
                          matTooltipPosition="right"
                          (click)="onCopyActivationCode(softwareVersion, selectedSubCtrl.value)"></span>
                </div>
                <div class="text-content second-line">{{ 'downloads.windows.shareable-url' | translate }} -
                    <span id="downloadUrlValue">{{ getDownloadURL(selectedSubCtrl.value, softwareVersion) }}</span>
                    <span id="subscription-url" class="mdi mdi-content-copy clickable icon copy-icon"
                          matTooltip="{{'downloads.copy-url-tooltip' | translate}}"
                          matTooltipPosition="right"
                          (click)="onCopyShareableUrl(selectedSubCtrl.value, softwareVersion)"></span>
                </div>
            </div>
        </ng-template>

         <!-- Agent Activation code-->
        <ng-template #agentActivationCodes let-softwareVersion="softwareVersion">
            <div class="section-header-content">
                <div class="text-content">{{'downloads.agent.activation-code' | translate}} -
                        {{ softwareVersion.feature_flags }}_{{ selectedSubCtrl.value.uuid }}_{{ softwareVersion.system }}
                    <span id="copyActivationCodeButton"
                          class="mdi mdi-content-copy clickable icon copy-icon"
                          matTooltip="{{'downloads.copy-activation-code-tooltip' | translate}}"
                          matTooltipPosition="right"
                          (click)="onCopyActivationCode(softwareVersion, selectedSubCtrl.value)"></span>
                </div>
                <div class="text-content second-line">{{'downloads.agent.shareable-url' | translate}} -
                    <span id="downloadAgentUrlValue">{{ getAgentDownloadURL(selectedSubCtrl.value, softwareVersion) }}</span>
                    <span id="subscription-agent-url" class="mdi mdi-content-copy clickable icon copy-icon"
                          matTooltip="{{'downloads.copy-url-tooltip' | translate}}"
                          matTooltipPosition="right"
                          (click)="onCopyShareableAgentUrl(selectedSubCtrl.value, softwareVersion)"></span>
                </div>
            </div>
        </ng-template>
        <!-- Agent Downloads-->
        <ng-template #agentSubscription let-softwareVersion="softwareVersion">
            <mat-card appearance="raised">
                <mat-card-content>
                    <ng-container *ngTemplateOutlet="windowsHeader"></ng-container>
                    <ng-container *ngTemplateOutlet="agentActivationCodes; context: {softwareVersion}"></ng-container>
                        <!-- Agent Installers -->
                        @for (winAgentVersion of softwareVersion?.win_agent_versions; track $index) {
                            <div class="download-wrapper">
                                <span class="mdi mdi-download download-icon icon"></span>
                                <span (click)="onDownloadAgent(selectedSubCtrl.value, winAgentVersion.url, softwareVersion, 'download_win_agent_exe')"
                                      class="clickable pseudo-link">{{ winAgentVersion.name }} {{ winAgentVersion.version }}
                                </span>&nbsp;(EXE)
                            </div>
                            <div *ngIf="winAgentVersion.url !== null" class="text-content">
                                <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                                <a class="link" href="{{freshDeskAgentGuideUrl}}" target="_blank"
                                   rel="noopener noreferrer">{{ freshDeskAgentGuideUrl }}</a>
                            </div>
                            <div class="download-wrapper">
                                <span class="mdi mdi-download download-icon icon"></span>
                                <span (click)="onDownloadAgent(selectedSubCtrl.value, winAgentVersion.msi_url, softwareVersion, 'download_win_agent_msi')"
                                      class="clickable pseudo-link">{{ winAgentVersion.name }} {{ winAgentVersion.version }}
                                </span>&nbsp;{{'downloads.msi.command-line-only' | translate}}
                            </div>
                            <div class="msi-install-label text-content">{{'downloads.agent.msi-installation-command' | translate}}
                                <mat-checkbox class="msi-install-pba-check"
                                              labelPosition="before"
                                              [(ngModel)]="pbaEnabled"
                                              color="primary">PBA</mat-checkbox>
                                <mat-checkbox class="msi-install-fde-check"
                                              labelPosition="before"
                                              [(ngModel)]="fdeEnabled"
                                              color="primary">FDE</mat-checkbox>
                            </div>
                            <div class="text-content agent-command">
                                <span>
                                    {{ getAgentInstallerCommand(softwareVersion, winAgentVersion.msi_url, selectedSubCtrl.value.uuid) }}
                                    <span class="mdi mdi-content-copy clickable icon copy-icon"
                                          matTooltip="{{'downloads.copy-install-command-tooltip' | translate}}"
                                          matTooltipPosition="right"
                                          (click)="onCopyAgentActivationCode(softwareVersion, winAgentVersion.msi_url, selectedSubCtrl.value.uuid)">
                                    </span>
                                </span>
                            </div>
                            <div class="second-line text-content">
                                <span>{{ 'downloads.windows.installation-doc' | translate }}&nbsp;</span>
                                <a class="link" href="{{freshDeskAgentGuideUrl}}" target="_blank"
                                   rel="noopener noreferrer">{{ freshDeskAgentGuideUrl }}</a>
                            </div>
                        }
                        <!-- Windows CLI -->
                        <div *ngIf="configurationService.showCliDownload && softwareVersion?.win_cli_versions.length > 0">
                            @for (winCliVersion of softwareVersion?.win_cli_versions; track $index) {
                                <div class="download-wrapper">
                                    <span class="mdi mdi-download download-icon icon"></span>
                                    <span (click)="onDownloadAsset(selectedSubCtrl.value, winCliVersion.url, 'download_win_cli')"
                                          class="clickable pseudo-link">{{ winCliVersion.name }} {{ winCliVersion.version }}
                                    </span>
                                </div>
                                <div class="text-content">
                                    <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                                    <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                                       rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                                </div>
                            }
                        </div>
                </mat-card-content>
            </mat-card>

            <mat-card appearance="raised" *ngIf="configurationService.showCliDownload &&
                          (softwareVersion?.ubuntu_cli_versions.length > 0 ||
                          softwareVersion?.rhel_cli_versions.length > 0)">
                <ng-container *ngTemplateOutlet="linuxHeader"></ng-container>
                <!-- Ubuntu Agent -->
                @for (ubuntu_agent_version of softwareVersion?.ubuntu_agent_versions; track $index) {
                    <div *ngIf="ubuntu_agent_version.url !== null" class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, ubuntu_agent_version.url, 'download_ubuntu_cli')"
                              class="clickable pseudo-link">{{ ubuntu_agent_version.name }} {{ ubuntu_agent_version.version }}
                        </span>
                    </div>
                    <div *ngIf="ubuntu_agent_version.url !== null" class="text-content">
                        <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                    </div>
                }
                <!-- RHEL Agent -->
                @for (rhel_agent_version of softwareVersion?.rhel_agent_versions; track $index) {
                    <div *ngIf="rhel_agent_version.url !== null" class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, rhel_agent_version.url, 'download_rhel_cli')"
                              class="clickable pseudo-link">{{ rhel_agent_version.name }} {{ rhel_agent_version.version }}
                        </span>
                    </div>
                    <div *ngIf="rhel_agent_version.url !== null" class="text-content">
                        <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                    </div>
                }
                <!-- Ubuntu CLI -->
                @for (ubuntu_cli_version of softwareVersion?.ubuntu_cli_versions; track $index) {
                    <div *ngIf="ubuntu_cli_version.url !== null" class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, ubuntu_cli_version.url, 'download_ubuntu_cli')"
                              class="clickable pseudo-link">{{ ubuntu_cli_version.name }} {{ ubuntu_cli_version.version }}
                        </span>
                    </div>
                    <div *ngIf="ubuntu_cli_version.url !== null" class="text-content">
                        <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                    </div>
                }
                <!-- RHEL CLI -->
                @for (rhel_cli_version of softwareVersion?.rhel_cli_versions; track $index) {
                    <div *ngIf="rhel_cli_version.url !== null" class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, rhel_cli_version.url, 'download_rhel_cli')"
                              class="clickable pseudo-link">{{ rhel_cli_version.name }} {{ rhel_cli_version.version }}
                        </span>
                    </div>
                    <div *ngIf="rhel_cli_version.url !== null" class="text-content">
                        <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                    </div>
                }
            </mat-card>
        </ng-template>

        <ng-template #d3eSubscription let-softwareVersion="softwareVersion" let-showActivationCodes="showActivationCodes" let-showDataDefense="showDataDefense">
            <mat-card appearance="raised">
                <ng-container *ngTemplateOutlet="windowsHeader"></ng-container>
                @if (showActivationCodes) {
                    <ng-container *ngTemplateOutlet="activationCodes; context: {softwareVersion}"></ng-container>
                }
                @if (showDataDefense) {
                    <!-- Data Defense EXE -->
                    <div class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span id="downloadD3EButton" class="clickable pseudo-link"
                              (click)="onDownloadD3E(selectedSubCtrl.value, softwareVersion)">Data Defense v{{ softwareVersion.major }}.{{ softwareVersion.minor }}.{{ softwareVersion.revision }}</span>
                        <span>&nbsp;(EXE)</span>
                    </div>
                    <div class="text-content">
                        <span>{{ 'downloads.windows.customized-installer' | translate }}</span>
                    </div>

                    <!-- Data Defense MSI -->
                    <div class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span class="clickable pseudo-link"
                              (click)="onDownloadAsset(selectedSubCtrl.value, softwareVersion.msi_url, 'download_msi')">Data Defense v{{ softwareVersion.major }}.{{ softwareVersion.minor }}.{{ softwareVersion.revision }}</span>
                        <span>&nbsp;{{ 'downloads.windows.command-line-only' | translate }}</span>
                    </div>

                    <div class="text-content">
                        <span>{{ 'downloads.windows.installation-command' | translate }}&nbsp;</span>
                        <span>
                            <span>{{ softwareVersion?.msi_url.trim().split('/').pop() }}</span>
                            <span>&nbsp;{{ installCommand }}</span>
                            @if (showActivationCodes) {
                                <!--
                                    Keep below as a single line or it adds extra spaces in the text.
                                    Could probably fix with css if you want to format the line.
                                -->
                                <span>&nbsp;{{ activationCommand }}{{ softwareVersion.feature_flags }}_{{ selectedSubCtrl.value.uuid }}_{{ softwareVersion.system }}</span>
                            }
                            <span class="mdi mdi-content-copy clickable icon copy-icon"
                                  matTooltip="{{'downloads.copy-install-command-tooltip' | translate}}"
                                  matTooltipPosition="right"
                                  (click)="onCopyMsiInstallCommand(selectedSubCtrl.value, softwareVersion)">
                            </span>
                        </span>
                    </div>
                    <div class="second-line text-content">
                        <span>{{ 'downloads.windows.installation-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskMsiGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskMsiGuideUrl }}</a>
                    </div>
                }
                <!-- Windows CLI -->
                <div *ngIf="configurationService.showCliDownload && softwareVersion?.win_cli_versions.length > 0">
                    @for (winCliVersion of softwareVersion?.win_cli_versions; track $index) {
                        <div class="download-wrapper">
                            <span class="mdi mdi-download download-icon icon"></span>
                            <span (click)="onDownloadAsset(selectedSubCtrl.value, winCliVersion.url, 'download_win_cli')"
                                  class="clickable pseudo-link">{{ winCliVersion.name }} {{ winCliVersion.version }}
                            </span>
                        </div>
                        <div class="text-content">
                            <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                            <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                               rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                        </div>
                    }
                </div>
            </mat-card>

            <mat-card appearance="raised" *ngIf="configurationService.showCliDownload &&
                          (softwareVersion?.ubuntu_cli_versions.length > 0 ||
                          softwareVersion?.rhel_cli_versions.length > 0)">
                <ng-container *ngTemplateOutlet="linuxHeader"></ng-container>
                <!-- Ubuntu CLI -->
                @for (ubuntu_cli_version of softwareVersion?.ubuntu_cli_versions; track $index) {
                    <div *ngIf="ubuntu_cli_version.url !== null" class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, ubuntu_cli_version.url, 'download_ubuntu_cli')"
                              class="clickable pseudo-link">{{ ubuntu_cli_version.name }} {{ ubuntu_cli_version.version }}
                        </span>
                    </div>
                    <div *ngIf="ubuntu_cli_version.url !== null" class="text-content">
                        <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                    </div>
                }
                <!-- RHEL CLI -->
                @for (rhel_cli_version of softwareVersion?.rhel_cli_versions; track $index) {
                    <div *ngIf="rhel_cli_version.url !== null" class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, rhel_cli_version.url, 'download_rhel_cli')"
                              class="clickable pseudo-link">{{ rhel_cli_version.name }} {{ rhel_cli_version.version }}
                        </span>
                    </div>
                    <div *ngIf="rhel_cli_version.url !== null" class="text-content">
                        <span>{{ 'downloads.installation-and-doc' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskCliGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskCliGuideUrl }}</a>
                    </div>
                }
            </mat-card>
        </ng-template>

        <ng-template #hardwareSubscription let-softwareVersion="softwareVersion">
            <mat-card appearance="raised">
                <ng-container *ngTemplateOutlet="nonOsSpecificHeader"></ng-container>
                <!-- PBA Installers -->
                @for (pbaVersion of softwareVersion?.pba_versions; track $index) {
                    <div class="download-wrapper">
                        <span class="mdi mdi-download download-icon icon"></span>
                        <span (click)="onDownloadAsset(selectedSubCtrl.value, pbaVersion.url, 'download_pba')"
                              class="clickable pseudo-link">{{ pbaVersion.name }} {{ pbaVersion.version }}
                        </span>
                    </div>
                    <div class="text-content">
                        <span>{{ 'downloads.other-os.install-instructions' | translate }}&nbsp;</span>
                        <a class="link" href="{{freshDeskPbaGuideUrl}}" target="_blank"
                           rel="noopener noreferrer">{{ freshDeskPbaGuideUrl }}</a>
                    </div>
                }
            </mat-card>
        </ng-template>

        @if (softwareVersion$ | async; as softwareVersion) {
            <div class="mat-card-wrapper">
                @switch (selectedSubCtrl.value.stripe_product.tier) {
                    @case (StripeProductTier.HARDWARE) {
                        <ng-container *ngTemplateOutlet="d3eSubscription; context: {softwareVersion: softwareVersion, showActivationCodes: false, showDataDefense: false}"></ng-container>
                        @if (configurationService.showPbaDownload && softwareVersion?.pba_versions.length > 0) {
                            <ng-container *ngTemplateOutlet="hardwareSubscription; context: {softwareVersion: softwareVersion}"></ng-container>
                        }
                    }
                    @case (StripeProductTier.HARDWARE2) {
                        <ng-container *ngTemplateOutlet="d3eSubscription; context: {softwareVersion: softwareVersion, showActivationCodes: false, showDataDefense: false}"></ng-container>
                        @if (configurationService.showPbaDownload && softwareVersion?.pba_versions.length > 0) {
                            <ng-container *ngTemplateOutlet="hardwareSubscription; context: {softwareVersion: softwareVersion}"></ng-container>
                        }
                    }
                    @case (StripeProductTier.FDE) {
                        <ng-container *ngTemplateOutlet="fdeSubscription; context: {softwareVersion: softwareVersion}"></ng-container>
                    }
                    @case (StripeProductTier.AGENT) {
                        <ng-container *ngTemplateOutlet="agentSubscription; context: {softwareVersion: softwareVersion}"></ng-container>
                    }
                    @default {
                        <ng-container *ngTemplateOutlet="d3eSubscription; context: {softwareVersion: softwareVersion, showActivationCodes: true, showDataDefense: true}"></ng-container>
                        @if (configurationService.showPbaDownload && softwareVersion?.pba_versions.length > 0) {
                            <ng-container *ngTemplateOutlet="hardwareSubscription; context: {softwareVersion: softwareVersion}"></ng-container>
                        }
                    }
                }
            </div>
        }

        <h2 *ngIf="subscriptions.length === 0">{{ 'L_NONE' | translate }}</h2>
        <h2 *ngIf="selectedSubCtrl.value && isSubExpired(selectedSubCtrl.value)">{{ 'downloads.expired' | translate }}</h2>
        <h2 *ngIf="selectedSubCtrl.value && !hasAvailableLicenses(selectedSubCtrl.value)">{{ 'downloads.no-licenses' | translate }}</h2>
    </div>
    <ng-template #skeletonLoaderForSubs>
        <ngx-skeleton-loader count="1" appearance="line"
                             [theme]="{width: '35%', height: '50px', margin: '28px 0 0 50px'}"></ngx-skeleton-loader>
        <ngx-skeleton-loader
                count="3"
                appearance="line"
                [theme]="{
        height: '105px',
        width: '80%',
        margin: '15px 25px 25px 25px'
    }">
        </ngx-skeleton-loader>
    </ng-template>
</ng-template>
